import React, { useEffect, useMemo, useState } from 'react';
import { AppShell } from '@/components/nav/AppShell';
import { jobsApi, customersApi, propertiesApi, usersApi } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Briefcase, Calendar, KanbanSquare, List } from 'lucide-react';
import { JOBS } from '@/constants/testIds';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { EmptyState } from '@/components/empty/EmptyState';
import { ListSkeleton } from '@/components/common/ListSkeleton';
import { JobStatusPill } from '@/components/jobs/JobStatusPill';
import { fmtDate } from '@/lib/format';
import { useAuth } from '@/lib/auth';

const FILTERS = [
  { key: 'all', label: 'All', testid: JOBS.filterAll },
  { key: 'scheduled', label: 'Scheduled', testid: JOBS.filterScheduled },
  { key: 'in_progress', label: 'In progress', testid: JOBS.filterInProgress },
  { key: 'finished', label: 'Finished', testid: JOBS.filterFinished },
  { key: 'invoiced', label: 'Invoiced' },
  { key: 'paid', label: 'Paid' },
  { key: 'cancelled', label: 'Cancelled' },
];

export default function JobsPage() {
  const [jobs, setJobs] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [cleaners, setCleaners] = useState([]);
  const [filter, setFilter] = useState('all');
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ customer_id: '', property_id: '', assigned_cleaner_id: '', scheduled_at: '', duration_hours: 3, notes: '' });
  const navigate = useNavigate();
  const { user } = useAuth();

  const load = async () => {
    try {
      const [js, cs, ps] = await Promise.all([
        jobsApi.list(filter === 'all' ? {} : { status: filter }),
        customersApi.list(),
        propertiesApi.list(),
      ]);
      setJobs(js);
      setCustomers(cs);
      setProperties(ps);
      if (user?.role === 'admin') {
        try { setCleaners((await usersApi.list()).filter(u => u.role === 'cleaner')); } catch {}
      }
    } catch {
      // surfaced by the api response interceptor
    } finally {
      setLoaded(true);
    }
  };
  useEffect(() => { load(); }, [filter]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.assigned_cleaner_id) payload.assigned_cleaner_id = null;
      if (!payload.property_id) payload.property_id = null;
      if (!payload.scheduled_at) payload.scheduled_at = null;
      const j = await jobsApi.create(payload);
      toast.success('Job created');
      setOpenNew(false);
      setForm({ customer_id: '', property_id: '', assigned_cleaner_id: '', scheduled_at: '', duration_hours: 3, notes: '' });
      navigate(`/jobs/${j.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create');
    }
  };

  const propsForCustomer = useMemo(() => {
    if (!form.customer_id) return properties;
    return properties.filter(p => p.customer_id === form.customer_id);
  }, [properties, form.customer_id]);

  return (
    <AppShell title="Jobs">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between [&>*:last-child]:w-full sm:[&>*:last-child]:w-auto mb-6">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold">Jobs</h1>
          <p className="text-sm text-muted-foreground">Manage cleaning jobs and their lifecycle.</p>
        </div>
        {user?.role === 'admin' && (
          <Dialog open={openNew} onOpenChange={setOpenNew}>
            <DialogTrigger asChild>
              <Button data-testid={JOBS.createButton}><Plus className="h-4 w-4 mr-2" />New job</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>New job</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <div>
                  <Label>Customer *</Label>
                  <select
                    className="w-full h-10 sm:h-9 rounded-md border border-input bg-background px-3 text-sm shadow-xs transition-colors hover:border-border-strong"
                    value={form.customer_id}
                    onChange={e => setForm({ ...form, customer_id: e.target.value, property_id: '' })}
                    data-testid={JOBS.formCustomer}
                    required
                  >
                    <option value="">Select customer</option>
                    {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Property</Label>
                  <select
                    className="w-full h-10 sm:h-9 rounded-md border border-input bg-background px-3 text-sm shadow-xs transition-colors hover:border-border-strong"
                    value={form.property_id}
                    onChange={e => setForm({ ...form, property_id: e.target.value })}
                    data-testid={JOBS.formProperty}
                  >
                    <option value="">Select property (optional)</option>
                    {propsForCustomer.map(p => <option key={p.id} value={p.id}>{p.nickname}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Assigned cleaner</Label>
                  <select
                    className="w-full h-10 sm:h-9 rounded-md border border-input bg-background px-3 text-sm shadow-xs transition-colors hover:border-border-strong"
                    value={form.assigned_cleaner_id}
                    onChange={e => setForm({ ...form, assigned_cleaner_id: e.target.value })}
                    data-testid={JOBS.formCleaner}
                  >
                    <option value="">Unassigned</option>
                    {cleaners.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <div><Label>Scheduled at</Label><Input type="datetime-local" value={form.scheduled_at} onChange={e => setForm({ ...form, scheduled_at: e.target.value })} data-testid={JOBS.formSchedule} /></div>
                <div><Label>Duration (hours)</Label><Input type="number" min="1" value={form.duration_hours} onChange={e => setForm({ ...form, duration_hours: parseInt(e.target.value) || 1 })} /></div>
                <div><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpenNew(false)}>Cancel</Button>
                  <Button type="submit" data-testid={JOBS.formSubmit}>Create</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="mb-4 flex items-center gap-2 flex-wrap">
        {/* Seven pills are ~520px; without the scroll the strip runs off a phone
            and "Cancelled" is unreachable. */}
        <div className="flex gap-1 border rounded-lg p-1 bg-card max-w-full overflow-x-auto no-scrollbar">
          {FILTERS.map(f => (
            <button
              key={f.key}
              data-testid={f.testid}
              onClick={() => setFilter(f.key)}
              className={`shrink-0 whitespace-nowrap px-3 py-1.5 rounded-md text-sm transition-colors ${filter === f.key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="sm:ml-auto flex gap-1 border rounded-lg p-1 bg-card">
          <Link to="/jobs" data-testid={JOBS.viewList} className="px-2 py-1 rounded-md text-xs bg-primary text-primary-foreground flex items-center gap-1"><List className="h-3 w-3" />List</Link>
          <Link to="/calendar" data-testid={JOBS.viewCalendar} className="px-2 py-1 rounded-md text-xs hover:bg-muted flex items-center gap-1"><Calendar className="h-3 w-3" />Calendar</Link>
          <Link to="/pipeline" data-testid={JOBS.viewKanban} className="px-2 py-1 rounded-md text-xs hover:bg-muted flex items-center gap-1"><KanbanSquare className="h-3 w-3" />Kanban</Link>
        </div>
      </div>

      {!loaded ? (
        <ListSkeleton />
      ) : jobs.length === 0 ? (
        <EmptyState
          icon={Briefcase}
          title="No jobs found"
          description="No jobs match this filter. Create one or sync an iCal calendar."
          cta={user?.role === 'admin' ? <Button onClick={() => setOpenNew(true)}><Plus className="h-4 w-4 mr-2" />New job</Button> : null}
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="divide-y">
              {jobs.map(j => {
                const c = customers.find(x => x.id === j.customer_id);
                const p = properties.find(x => x.id === j.property_id);
                return (
                  <Link key={j.id} to={`/jobs/${j.id}`} className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-muted/40">
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">{c?.name || 'Unknown'} • {p?.nickname || 'No property'}</div>
                      <div className="text-xs text-muted-foreground truncate">{fmtDate(j.scheduled_at)} • {j.duration_hours || 3}h</div>
                    </div>
                    <JobStatusPill status={j.status} />
                  </Link>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </AppShell>
  );
}
